import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '@/context/PlatformAuthContext';
import PlatformLayout from '@/components/layout/PlatformLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Loader2, Building2, Search, ChevronLeft, ChevronRight, 
  Users, Receipt, Eye, Mail, Phone, Calendar, Package
} from 'lucide-react';
import { toast } from 'sonner';

const Customers = () => {
  const { fetchWithAuth } = usePlatformAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const pageSize = 10;

  useEffect(() => {
    loadCustomers();
  }, [page, search]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      let url = `/api/platform/customers?page=${page}&page_size=${pageSize}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const response = await fetchWithAuth(url);
      if (!response.ok) throw new Error('Failed to load customers');
      
      const data = await response.json();
      setCustomers(data.customers || []);
      setTotalPages(Math.ceil(data.total / pageSize));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const viewCustomerDetail = async (customerId) => {
    setSelectedCustomer(customerId);
    setDetailLoading(true);
    
    try {
      const response = await fetchWithAuth(`/api/platform/customers/${customerId}`);
      if (!response.ok) throw new Error('Failed to load customer details');
      
      const data = await response.json();
      setCustomerDetail(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const statusColors = {
    ACTIVE: 'bg-green-500/10 text-green-400 border-green-500/20',
    DEMO: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    PAST_DUE: 'bg-red-500/10 text-red-400 border-red-500/20',
    CANCELLED: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
    EXPIRED: 'bg-orange-500/10 text-orange-400 border-orange-500/20'
  };

  const tierColors = {
    DEMO: 'bg-purple-500/10 text-purple-400',
    STARTER: 'bg-blue-500/10 text-blue-400',
    GROWTH: 'bg-green-500/10 text-green-400',
    TRADER: 'bg-orange-500/10 text-orange-400',
    BUSINESS: 'bg-cyan-500/10 text-cyan-400',
    ENTERPRISE: 'bg-pink-500/10 text-pink-400'
  };

  return (
    <PlatformLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Customers</h1>
            <p className="text-slate-400 mt-1">Manage all customer organizations</p>
          </div>
          
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search customers..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              data-testid="customer-search"
            />
          </div>
        </div>

        {/* Customers Table */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : customers.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No customers found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-slate-700">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Organization</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Plan</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Status</th>
                      <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Usage</th>
                      <th className="text-right px-6 py-4 text-sm font-medium text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {customers.map((customer) => (
                      <tr key={customer.organization_id} className="hover:bg-slate-700/50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-white">{customer.name}</p>
                            <p className="text-sm text-slate-400">{customer.organization_id}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {customer.subscription ? (
                            <Badge className={tierColors[customer.subscription.plan_tier] || 'bg-slate-600'}>
                              {customer.subscription.plan_name}
                            </Badge>
                          ) : (
                            <span className="text-slate-500">No Plan</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {customer.subscription ? (
                            <Badge className={statusColors[customer.subscription.status] || 'bg-slate-600'}>
                              {customer.subscription.status}
                            </Badge>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {customer.subscription ? (
                            <span className="text-slate-300">
                              {customer.subscription.transactions_used} / {customer.subscription.monthly_limit}
                            </span>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewCustomerDetail(customer.organization_id)}
                            className="text-slate-400 hover:text-white hover:bg-slate-700"
                            data-testid={`view-customer-${customer.organization_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-slate-400">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* Customer Detail Dialog */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
        <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
            <DialogDescription className="text-slate-400">
              Detailed information about this organization
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : customerDetail ? (
            <div className="space-y-6">
              {/* Organization Info */}
              <div className="p-4 rounded-lg bg-slate-700/50">
                <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Organization
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">Name</p>
                    <p className="text-white">{customerDetail.organization?.name}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Type</p>
                    <p className="text-white">{customerDetail.organization?.business_type || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Subscription Info */}
              {customerDetail.subscription && (
                <div className="p-4 rounded-lg bg-slate-700/50">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Subscription
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-400">Plan</p>
                      <Badge className={tierColors[customerDetail.plan?.tier] || 'bg-slate-600'}>
                        {customerDetail.plan?.name}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-slate-400">Status</p>
                      <Badge className={statusColors[customerDetail.subscription.status] || 'bg-slate-600'}>
                        {customerDetail.subscription.status}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-slate-400">Transactions Used</p>
                      <p className="text-white">
                        {customerDetail.subscription.transactions_used_this_period} / {customerDetail.subscription.monthly_quota_limit}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-400">Billing Cycle</p>
                      <p className="text-white">{customerDetail.subscription.billing_cycle}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Users */}
              <div className="p-4 rounded-lg bg-slate-700/50">
                <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Users ({customerDetail.users?.length || 0})
                </h3>
                <div className="space-y-2">
                  {customerDetail.users?.slice(0, 5).map((user) => (
                    <div key={user.user_id} className="flex items-center justify-between text-sm">
                      <div>
                        <p className="text-white">{user.name}</p>
                        <p className="text-slate-400">{user.email}</p>
                      </div>
                      <Badge className="bg-slate-600 text-slate-300">{user.role}</Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sales Rep */}
              {customerDetail.sales_rep && (
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <h3 className="font-semibold text-green-400 mb-2">Referred By</h3>
                  <p className="text-white">{customerDetail.sales_rep.name}</p>
                  <p className="text-sm text-slate-400">
                    Coupon: {customerDetail.sales_rep.coupon_code}
                  </p>
                </div>
              )}

              {/* Recent Invoices */}
              {customerDetail.invoices?.length > 0 && (
                <div className="p-4 rounded-lg bg-slate-700/50">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    Recent Invoices
                  </h3>
                  <div className="space-y-2">
                    {customerDetail.invoices.slice(0, 3).map((invoice) => (
                      <div key={invoice.invoice_id} className="flex items-center justify-between text-sm">
                        <span className="text-slate-300">{invoice.invoice_number}</span>
                        <div className="flex items-center gap-2">
                          <Badge className={invoice.status === 'PAID' ? 'bg-green-500/10 text-green-400' : 'bg-orange-500/10 text-orange-400'}>
                            {invoice.status}
                          </Badge>
                          <span className="text-white">₹{(invoice.total / 100).toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </PlatformLayout>
  );
};

export default Customers;
